import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Squircle } from '@squircle-js/react';
import { Share2, Scale, Pill, Loader2 } from 'lucide-react';
import html2canvas from 'html2canvas-pro';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../functions/useSettings';
import { formatWeight } from '../../functions/formatWeight';
import KodaCareLogo from '../../components/Custom Icons/KodaCareLogo';
import { format } from 'date-fns';
import { useLanguage } from '../../functions/useLanguage';
import { useBean } from '../../context/BeanContext';
import { useToast } from '../../hooks/useToast';
import { usePet } from '../../context/PetContext';
import { DiaryEditor } from '../../components/DiaryEditor';
import { loadDiary, saveDiary } from '../../lib/diary';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MedicalProfile {
  fullName: string;
  profession: string;
  dob: string;
  transplantDate: string;
  hospitalName: string;
  processNumber: string;
  allergies: string;
  surgeon: string;
}

const PROFILE_DEFAULTS: MedicalProfile = {
  fullName: '',
  profession: '',
  dob: '',
  transplantDate: '',
  hospitalName: '',
  processNumber: '',
  allergies: '',
  surgeon: '',
};

export const MEDICAL_PROFILE_KEY = 'koda_medical_profile';
export const MEDICAL_PROFILE_EVENT = 'kodacare-medical-profile-updated';

function profileStorageKey(petId?: string | null): string {
  return petId ? `${MEDICAL_PROFILE_KEY}_${petId}` : MEDICAL_PROFILE_KEY;
}

export function loadMedicalProfile(petId?: string | null): MedicalProfile {
  try {
    const scoped = localStorage.getItem(profileStorageKey(petId));
    if (scoped) return { ...PROFILE_DEFAULTS, ...JSON.parse(scoped) };

    // Migration fallback for legacy single-profile key
    const legacy = localStorage.getItem(MEDICAL_PROFILE_KEY);
    if (legacy) return { ...PROFILE_DEFAULTS, ...JSON.parse(legacy) };

    return { ...PROFILE_DEFAULTS };
  } catch {
    return { ...PROFILE_DEFAULTS };
  }
}

export function saveMedicalProfile(profile: MedicalProfile, petId?: string | null) {
  const key = profileStorageKey(petId);
  localStorage.setItem(key, JSON.stringify(profile));

  // Cross-tab updates
  window.dispatchEvent(new StorageEvent('storage', { key, newValue: JSON.stringify(profile) }));
  // Same-tab updates
  window.dispatchEvent(new CustomEvent(MEDICAL_PROFILE_EVENT, {
    detail: { petId: petId ?? null, profile },
  }));
}

// ─── Inline-editable field ────────────────────────────────────────────────────

function EditableField({
  label,
  value,
  placeholder,
  onChange,
  readOnly,
}: {
  label: string;
  value: string;
  placeholder: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    if (readOnly) return;
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div className="flex flex-col gap-0.5">
      {label && <span className="text-[10px] font-bold uppercase tracking-wider text-white/50 truncate block">{label}</span>}
      {editing && !readOnly ? (
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          onBlur={() => setEditing(false)}
          placeholder={placeholder}
          className="bg-white/20 text-white placeholder-white/40 text-sm font-semibold rounded-lg px-2 py-1 outline-none w-full border border-white/30 focus:border-white/60"
        />
      ) : (
        <button
          onClick={handleClick}
          disabled={readOnly}
          className={
            readOnly
              ? 'text-left text-sm font-semibold text-white min-h-[28px] px-2 py-1 rounded-lg w-full truncate cursor-default opacity-90'
              : 'text-left text-sm font-semibold text-white min-h-[28px] px-2 py-1 rounded-lg hover:bg-white/10 transition-colors w-full truncate'
          }
        >
          {value || <span className={readOnly ? 'text-white/40 italic font-normal' : 'text-white/30 italic font-normal'}>{placeholder}</span>}
        </button>
      )}
    </div>
  );
}

// ─── Masked date field ───────────────────────────────────────────────────────

function DateField({
  label,
  value,
  dateFormat,
  onChange,
  readOnly,
}: {
  label: string;
  value: string;
  dateFormat: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Derive separator and placeholder from the chosen format
  const sep = dateFormat.includes('/') ? '/' : '-';
  const placeholder = dateFormat; // e.g. DD/MM/YYYY

  const handleClick = () => {
    if (readOnly) return;
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Strip everything except digits
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    let masked = '';
    for (let i = 0; i < digits.length; i++) {
      if (i === 2 || i === 4) masked += sep;
      masked += digits[i];
    }
    onChange(masked);
  };

  return (
    <div className="flex flex-col gap-0.5">
      {label && <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">{label}</span>}
      {editing && !readOnly ? (
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={value}
          onChange={handleChange}
          onBlur={() => setEditing(false)}
          placeholder={placeholder}
          maxLength={10}
          className="bg-white/20 text-white placeholder-white/40 text-sm font-semibold rounded-lg px-2 py-1 outline-none w-full border border-white/30 focus:border-white/60"
        />
      ) : (
        <button
          onClick={handleClick}
          disabled={readOnly}
          className={
            readOnly
              ? 'text-left text-sm font-semibold text-white min-h-[28px] px-2 py-1 rounded-lg w-full cursor-default opacity-90'
              : 'text-left text-sm font-semibold text-white min-h-[28px] px-2 py-1 rounded-lg hover:bg-white/10 transition-colors w-full'
          }
        >
          {value || <span className={readOnly ? 'text-white/40 italic font-normal' : 'text-white/30 italic font-normal'}>{placeholder}</span>}
        </button>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function MedicalCard() {
  const { logs, medications } = useData();
  const { user, updateUser } = useAuth();
  const { activePet, activePetId, updatePet, isActivePetSubordinate } = usePet();
  const { showToast, ToastMount } = useToast();
  const { awardXP } = useBean();
  const { isMetric, dateFormat } = useSettings();
  const { t } = useLanguage();
  const cardRef = useRef<HTMLDivElement>(null);
  const [profile, setProfile] = useState<MedicalProfile>(() => loadMedicalProfile(activePetId));
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [diaryContent, setDiaryContent] = useState<string>(() => loadDiary(activePetId));
  const [diarySaved, setDiarySaved] = useState(false);
  const diarySaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-load local profile + diary when active pet changes
  useEffect(() => {
    setProfile(loadMedicalProfile(activePetId));
    setDiaryContent(loadDiary(activePetId));
  }, [activePetId]);

  // Seed all fields from Firebase whenever the active pet or user object updates
  useEffect(() => {
    if (!activePet && !user) return;
    setProfile(prev => ({
      fullName:       user?.full_name       || prev.fullName       || '',
      // Pet identity fields — live on the pet doc
      profession:     activePet?.name        || prev.profession     || '',
      transplantDate: activePet?.dob         || prev.transplantDate || '',
      dob:            activePet?.breed        || prev.dob            || '',
      // Vet / health fields — now stored on the pet doc (fallback to user doc for legacy data)
      hospitalName:   activePet?.vet_practice || user?.hospital_name  || prev.hospitalName   || '',
      processNumber:  activePet?.microchip    || user?.process_number || prev.processNumber  || '',
      allergies:      activePet?.allergies    || user?.allergies      || prev.allergies      || '',
      surgeon:        activePet?.primary_vet  || user?.surgeon        || prev.surgeon        || '',
    }));
  }, [
    activePet?.name, activePet?.dob, activePet?.breed,
    activePet?.vet_practice, activePet?.microchip, activePet?.allergies, activePet?.primary_vet,
    user?.full_name, user?.hospital_name, user?.process_number, user?.allergies, user?.surgeon,
  ]);

  // ── One-time migration: copy vet fields from user doc → pet doc (owner only) ──
  useEffect(() => {
    if (isActivePetSubordinate || !activePetId || !user) return;
    if (activePet?.vet_practice !== undefined) return; // already on pet doc
    const migrated: { vet_practice?: string; microchip?: string; primary_vet?: string; allergies?: string } = {};
    if (user.hospital_name) migrated.vet_practice = user.hospital_name;
    if (user.process_number) migrated.microchip = user.process_number;
    if (user.surgeon) migrated.primary_vet = user.surgeon;
    if (user.allergies) migrated.allergies = user.allergies;
    if (Object.keys(migrated).length > 0) updatePet(activePetId, migrated).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePetId, isActivePetSubordinate]);

  const update = useCallback((field: keyof MedicalProfile) => (value: string) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleDiaryChange = useCallback((html: string) => {
    setDiaryContent(html);
    if (diarySaveTimer.current) clearTimeout(diarySaveTimer.current);
    diarySaveTimer.current = setTimeout(() => {
      saveDiary(html, activePetId);
      setDiarySaved(true);
      setTimeout(() => setDiarySaved(false), 2000);
    }, 800);
  }, [activePetId]);

  const handleSave = async () => {
    if (saving || !user) return;
    setSaving(true);
    try {
      // Persist to localStorage for offline use
      saveMedicalProfile(profile, activePetId);

      if (activePetId && !isActivePetSubordinate) {
        await updatePet(activePetId, {
          name:         profile.profession    || activePet?.name || 'Pet',
          breed:        profile.dob           || undefined,
          dob:          profile.transplantDate || undefined,
          vet_practice: profile.hospitalName  || undefined,
          microchip:    profile.processNumber || undefined,
          primary_vet:  profile.surgeon       || undefined,
          allergies:    profile.allergies     || undefined,
        });
      }

      // Only the owner's personal name stays on the user doc
      await updateUser({
        full_name: profile.fullName || undefined,
      });
      await awardXP('medical_card');
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('[MedicalCard] save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  // ── Stats computed from all logs (same logic as Trends) ───────────────────
  const { avgWeight, weightChange } = useMemo(() => {
    const weights = logs.filter(l => l.weight_kg != null).map(l => l.weight_kg as number);
    const avgW = weights.length > 0 ? weights.reduce((a, b) => a + b, 0) / weights.length : null;
    const weightChange = weights.length > 1 ? weights[weights.length - 1] - weights[0] : null;
    return { avgWeight: avgW, weightChange };
  }, [logs]);

  // ── Share as image ─────────────────────────────────────────────────────────
  const handleShare = async () => {
    if (!cardRef.current || sharing) return;
    setSharing(true);
    setShareError(false);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        ignoreElements: (el) => el instanceof HTMLElement && el.dataset.noCapture !== undefined,
      });
      const blob: Blob = await new Promise((resolve, reject) =>
        canvas.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/png')
      );
      const fileName = `KodaCare-PetID-${format(new Date(), 'yyyy-MM-dd')}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });

      // Try system share sheet (only if files are actually supported)
      const canShareFiles = typeof navigator.canShare === 'function'
        && navigator.canShare({ files: [file] });
      if (navigator.share && canShareFiles) {
        try {
          await navigator.share({ files: [file], title: 'KodaCare Pet ID Card' });
        } catch (err) {
          // User dismissed the share sheet — do nothing
          if (err instanceof Error && err.name === 'AbortError') return;
          console.warn('[MedicalCard] navigator.share failed:', err);
        }
        return;
      }
      // No native share support — trigger download.
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.rel = 'noopener';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (err) {
        console.warn('[MedicalCard] download failed, opening in new tab:', err);
        window.open(url, '_blank', 'noopener');
        showToast('Image opened in new tab', 'info');
      } finally {
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      console.error('[MedicalCard] share failed:', err);
      setShareError(true);
      setTimeout(() => setShareError(false), 3000);
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Card */}
      <Squircle asChild cornerRadius={24} cornerSmoothing={1}>
        <div ref={cardRef} data-card className="overflow-hidden">
        <div data-card-gradient className="bg-primary-brown p-5 text-white space-y-3">

          {/* Header row */}
          <div className="flex items-center justify-between mb-8">
            <p className="text-xs font-bold opacity-60 uppercase tracking-widest">{t('medcard.title')}</p>
            <div className="flex items-center gap-1" data-no-capture>
              {!isActivePetSubordinate && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors disabled:opacity-50 bg-white/20 hover:bg-white/30 text-white"
                  title={t('medcard.saveTitleAttr')}
                >
                  {saved ? t('medcard.saved') : saving ? t('medcard.saving') : t('medcard.save')}
                </button>
              )}
              <div className="flex flex-col items-end gap-0.5">
                <button
                  onClick={handleShare}
                  disabled={sharing}
                  className="p-1.5 hover:bg-white/20 rounded-full text-white/70 transition-colors"
                  style={{ opacity: sharing ? 0.4 : 1 }}
                  title={t('medcard.shareAlt')}
                >
                  {sharing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
                </button>
                {shareError && (
                  <span className="text-error-red\/50 text-[10px] leading-tight text-right">{t('medcard.shareFailed')}</span>
                )}
              </div>
            </div>
          </div>

          {/* Full name + Profession */}
          <div className="flex items-center gap-3 mb-4">
            {/* Initials badge */}
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white font-black text-lg tracking-tight select-none">
              {profile.fullName
                ? profile.fullName.trim().split(/\s+/).slice(0, 2).map(w => w[0].toUpperCase()).join('')
                : '?'}
            </div>
            {/* Name + Profession stacked */}
            <div className="flex-1 grid grid-cols-2 gap-3">
              <EditableField
                label={t('medcard.fullName')}
                value={profile.fullName}
                placeholder={t('medcard.placeholder.name')}
                onChange={update('fullName')}
              />
              <EditableField
                label={t('medcard.profession')}
                value={profile.profession}
                placeholder={t('medcard.placeholder.profession')}
                onChange={update('profession')}
                readOnly={isActivePetSubordinate}
              />
            </div>
          </div>

          {/* DOB | Transplant Date */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/10 rounded-xl p-3">
              <EditableField label={t('medcard.dob')} value={profile.dob} placeholder={t('medcard.placeholder.dob')} onChange={update('dob')} readOnly={isActivePetSubordinate} />
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <DateField label={t('medcard.transplantDate')} value={profile.transplantDate} dateFormat={dateFormat} onChange={update('transplantDate')} readOnly={isActivePetSubordinate} />
            </div>
          </div>

          {/* Hospital | Process No. */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/10 rounded-xl p-3">
              <EditableField label={t('medcard.hospital')} value={profile.hospitalName} placeholder={t('medcard.placeholder.hospital')} onChange={update('hospitalName')} readOnly={isActivePetSubordinate} />
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <EditableField label={t('medcard.processNo')} value={profile.processNumber} placeholder={t('medcard.placeholder.processNo')} onChange={update('processNumber')} readOnly={isActivePetSubordinate} />
            </div>
          </div>

          {/* Surgeon | Allergies */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            <div className="bg-white/10 rounded-xl p-3">
              <EditableField label={t('medcard.surgeon')} value={profile.surgeon} placeholder={t('medcard.placeholder.surgeon')} onChange={update('surgeon')} readOnly={isActivePetSubordinate} />
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <EditableField label={t('medcard.allergies')} value={profile.allergies} placeholder={t('medcard.placeholder.allergies')} onChange={update('allergies')} readOnly={isActivePetSubordinate} />
            </div>
          </div>

          {/* Avg stats */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            <div className="bg-white/10 rounded-xl p-3 flex flex-col gap-1">
              <div className="flex items-center gap-1.5 text-[10px] font-bold opacity-70 uppercase tracking-wider min-w-0">
                <Scale size={11} className="flex-shrink-0" />
                <span className="truncate">{t('medcard.avgWeight')}</span>
              </div>
              <div className="text-xl font-bold font-mono">
                {avgWeight != null ? formatWeight(avgWeight, isMetric) : '--'}
              </div>
              <div className="text-[10px] opacity-70">
                {weightChange != null ? (
                  <span className="text-white/80">
                    {weightChange > 0 ? '↑' : weightChange < 0 ? '↓' : '→'}{' '}
                    {Math.abs(weightChange).toFixed(1)} {isMetric ? 'kg' : 'lbs'}
                  </span>
                ) : <span className="opacity-50">--</span>}
              </div>
            </div>
            <div className="bg-white/10 rounded-xl p-3 flex flex-col gap-1">
              <div className="flex items-center gap-1.5 text-[10px] font-bold opacity-70 uppercase tracking-wider min-w-0">
                <Pill size={11} className="flex-shrink-0" />
                <span className="truncate">{t('medcard.medications')}</span>
              </div>
              {medications.length > 0 ? (
                <div className="flex flex-col gap-1 mt-0.5">
                  {medications.slice(0, 4).map(med => (
                    <div key={med.id} className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: med.color_hex }} />
                      <span className="text-[11px] font-medium truncate leading-tight">{med.name}</span>
                    </div>
                  ))}
                  {medications.length > 4 && (
                    <span className="text-[10px] opacity-50">+{medications.length - 4} more</span>
                  )}
                </div>
              ) : (
                <span className="text-sm opacity-50 mt-1">--</span>
              )}
            </div>
          </div>

          {/* KodaCare footer */}
          <div className="pt-3 border-t border-white/20 flex items-center justify-between">
            <KodaCareLogo className="text-background-light" style={{ height: 14, width: 'auto' }} />
            <span className="text-[10px] opacity-50 tracking-wide">kodacare.pet</span>
          </div>
        </div>
      </div>
      </Squircle>

      <p className="text-center text-xs text-gray-400 mt-4">{t('medcard.hint')}</p>

      {/* Health Diary */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2 px-1">
          <h3 className="text-sm font-bold text-white/80 uppercase tracking-wider">{t('medcard.diary.title')}</h3>
          {diarySaved && <span className="text-xs text-white/50">{t('medcard.diary.saved')}</span>}
        </div>
        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
          <DiaryEditor
            content={diaryContent}
            onChange={handleDiaryChange}
            placeholder={t('medcard.diary.placeholder')}
          />
        </div>
      </div>

      <ToastMount />
    </div>
  );
}
